import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  aiObservabilityApi,
  type AiObservabilityEvent,
  type AiObservabilityListResponse,
  type AiObservabilityQuery,
  type AiObservabilityStatsResponse,
} from '../services/aiObservabilityApi';

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const formatUsd = (value?: number | null) => {
  const safe = Number(value || 0);
  return `$${safe.toFixed(4)}`;
};

const KpiCard: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
  <Paper sx={{ p: 2, flex: 1, minWidth: 180 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700} sx={color ? { color } : undefined}>
      {value}
    </Typography>
  </Paper>
);

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
};

const defaultTo = () => new Date().toISOString().slice(0, 10);

const AiObservabilityPage: React.FC = () => {
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);
  const [filters, setFilters] = useState<AiObservabilityQuery>({
    from: defaultFrom(),
    to: defaultTo(),
    page: 1,
    pageSize: 50,
    sortBy: 'startedAt',
    sortDir: 'desc',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listData, setListData] = useState<AiObservabilityListResponse | null>(null);
  const [stats, setStats] = useState<AiObservabilityStatsResponse | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<AiObservabilityEvent | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsRes, statsRes] = await Promise.all([
        aiObservabilityApi.listEvents(filters, accessToken),
        aiObservabilityApi.getStats(filters, accessToken),
      ]);
      setListData(eventsRes);
      setStats(statsRes);
    } catch (e: any) {
      setError(e?.message || 'Failed to load AI observability data');
    } finally {
      setLoading(false);
    }
  }, [filters, accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const providerModelChart = useMemo(
    () =>
      (stats?.byProviderModel || []).slice(0, 12).map((row) => ({
        name: `${row.provider}/${row.model}`,
        calls: row.calls,
        cost: row.estimatedCostUsd,
      })),
    [stats],
  );

  const endpointChart = useMemo(
    () =>
      (stats?.byEndpoint || []).slice(0, 12).map((row) => ({
        name: row.requestPath,
        calls: row.calls,
      })),
    [stats],
  );

  const timelineChart = useMemo(
    () => (stats?.timeline || []).map((row) => ({ day: row.day, calls: row.calls, cost: row.estimatedCostUsd })),
    [stats],
  );

  const openEvent = useCallback(
    async (id: string) => {
      try {
        const event = await aiObservabilityApi.getEventById(id, accessToken);
        setSelectedEvent(event);
        setDrawerOpen(true);
      } catch (e: any) {
        setError(e?.message || 'Failed to load event details');
      }
    },
    [accessToken],
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        AI Observability
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Filterable analytics for AI requests, payloads, model usage, latency, fallback behavior, and estimated costs.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
          <TextField
            label="From"
            type="date"
            size="small"
            value={filters.from || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value, page: 1 }))}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={filters.to || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value, page: 1 }))}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Provider"
            size="small"
            value={filters.provider || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, provider: e.target.value, page: 1 }))}
          />
          <TextField
            label="Model"
            size="small"
            value={filters.model || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, model: e.target.value, page: 1 }))}
          />
          <TextField
            label="Endpoint"
            size="small"
            value={filters.requestPath || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, requestPath: e.target.value, page: 1 }))}
          />
          <TextField
            label="Task"
            size="small"
            value={filters.taskType || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, taskType: e.target.value, page: 1 }))}
          />
          <TextField
            label="Search"
            size="small"
            value={filters.search || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              displayEmpty
              value={
                filters.success === undefined ? 'all' : filters.success ? 'true' : 'false'
              }
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  success: v === 'all' ? undefined : v === 'true',
                  page: 1,
                }));
              }}
            >
              <MenuItem value="all">Success: All</MenuItem>
              <MenuItem value="true">Success</MenuItem>
              <MenuItem value="false">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              displayEmpty
              value={
                filters.fallback === undefined ? 'all' : filters.fallback ? 'true' : 'false'
              }
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  fallback: v === 'all' ? undefined : v === 'true',
                  page: 1,
                }));
              }}
            >
              <MenuItem value="all">Fallback: All</MenuItem>
              <MenuItem value="true">Fallback</MenuItem>
              <MenuItem value="false">No fallback</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => fetchData()} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? <CircularProgress size={22} /> : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <KpiCard label="Calls" value={stats?.totals.totalCalls ?? 0} />
        <KpiCard label="Success Rate" value={`${stats?.totals.successRatePct ?? 0}%`} color="#2e7d32" />
        <KpiCard label="Fallback Rate" value={`${stats?.totals.fallbackRatePct ?? 0}%`} color="#ed6c02" />
        <KpiCard label="Avg / P95 Latency" value={`${stats?.totals.avgDurationMs ?? 0} / ${stats?.totals.p95DurationMs ?? 0} ms`} />
        <KpiCard label="Total Tokens" value={(stats?.totals.totalTokens ?? 0).toLocaleString()} />
        <KpiCard label="Estimated Cost" value={formatUsd(stats?.totals.totalEstimatedCostUsd)} />
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Calls & Cost Over Time
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={timelineChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="calls" stroke="#1976d2" fill="#90caf9" fillOpacity={0.4} />
              <Area type="monotone" dataKey="cost" stroke="#2e7d32" fill="#a5d6a7" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Top Models
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={providerModelChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" hide />
              <YAxis />
              <Tooltip />
              <Bar dataKey="calls" fill="#1976d2" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Top Endpoints
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={endpointChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" hide />
              <YAxis />
              <Tooltip />
              <Bar dataKey="calls" fill="#ed6c02" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">AI Call Events</Typography>
          <Typography variant="caption" color="text.secondary">
            total={listData?.total ?? 0}
          </Typography>
        </Stack>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Start</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Fallback</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="right">In/Out chars</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(listData?.items || []).map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell>{formatDateTime(row.startedAt)}</TableCell>
                  <TableCell>{row.requestPath}</TableCell>
                  <TableCell>{row.taskType || '-'}</TableCell>
                  <TableCell>{row.model || row.provider || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={row.isSuccess ? 'success' : 'error'}
                      label={row.isSuccess ? 'success' : 'failed'}
                    />
                  </TableCell>
                  <TableCell>
                    {row.isFallback ? <Chip size="small" color="warning" label="fallback" /> : '-'}
                  </TableCell>
                  <TableCell align="right">{row.durationMs ?? '-'}</TableCell>
                  <TableCell align="right">
                    {(row.inputChars ?? 0).toLocaleString()} / {(row.outputChars ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">{(row.totalTokens ?? 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{formatUsd(row.estimatedCostUsd)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEvent(row.id)}>
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: '100vw', md: 760 }, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            AI Event Detail
          </Typography>
          {!selectedEvent ? <CircularProgress size={20} /> : null}
          {selectedEvent ? (
            <Stack spacing={1}>
              <Typography variant="body2"><strong>ID:</strong> {selectedEvent.id}</Typography>
              <Typography variant="body2"><strong>Path:</strong> {selectedEvent.requestPath}</Typography>
              <Typography variant="body2"><strong>Task:</strong> {selectedEvent.taskType || '-'}</Typography>
              <Typography variant="body2"><strong>Provider/Model:</strong> {selectedEvent.provider || '-'} / {selectedEvent.model || '-'}</Typography>
              <Typography variant="body2"><strong>Processed By:</strong> {selectedEvent.processedBy || '-'}</Typography>
              <Typography variant="body2"><strong>Window:</strong> {formatDateTime(selectedEvent.startedAt)} → {formatDateTime(selectedEvent.endedAt)}</Typography>
              <Typography variant="body2"><strong>Latency:</strong> {selectedEvent.durationMs ?? '-'} ms</Typography>
              <Typography variant="body2"><strong>In/Out:</strong> {(selectedEvent.inputChars ?? 0).toLocaleString()} / {(selectedEvent.outputChars ?? 0).toLocaleString()} chars</Typography>
              <Typography variant="body2"><strong>Tokens:</strong> {(selectedEvent.totalTokens ?? 0).toLocaleString()} {selectedEvent.tokensEstimated ? '(estimated)' : '(provider usage)'}</Typography>
              <Typography variant="body2"><strong>Cost:</strong> {formatUsd(selectedEvent.estimatedCostUsd)}</Typography>
              {selectedEvent.errorMessage ? (
                <Alert severity="error">{selectedEvent.errorMessage}</Alert>
              ) : null}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Input payload</Typography>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                {safeJsonPretty(selectedEvent.requestPayload)}
              </Paper>
              <Typography variant="subtitle2">Output payload</Typography>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                {safeJsonPretty(selectedEvent.responsePayload)}
              </Paper>
              <Typography variant="subtitle2">Metadata</Typography>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                {safeJsonPretty(selectedEvent.metadata)}
              </Paper>
            </Stack>
          ) : null}
        </Box>
      </Drawer>
    </Box>
  );
};

const safeJsonPretty = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return String(value);
  }
};

export default AiObservabilityPage;
